import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";

export default function ApiExplorer() {
  useEffect(() => {
    document.title = "API Explorer · LegacyGrowth Engine";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5EF]" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <header className="border-b border-[#1F3A68]/10 bg-[#1E3A66] sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/api-docs"
              className="flex items-center gap-2 text-sm text-white/80 hover:text-[#D4A84B] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to docs
            </Link>
            <div className="h-6 w-px bg-white/20" />
            <h1 className="text-lg font-semibold text-white">API Explorer</h1>
            <span className="px-2 py-0.5 text-xs font-semibold bg-[#D4A84B] text-[#1E3A66] rounded">
              Interactive
            </span>
          </div>
          <a
            href="/v1/openapi.json"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-sm text-white/80 hover:text-[#D4A84B] transition-colors"
          >
            openapi.json
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </header>

      <main className="swagger-wrapper">
        <SwaggerUI
          url="/v1/openapi.json"
          docExpansion="list"
          defaultModelsExpandDepth={1}
          tryItOutEnabled
          persistAuthorization
        />
      </main>

      <style>{`
        :root {
          --lg-navy: #1E3A66;
          --lg-navy-dark: #142849;
          --lg-gold: #D4A84B;
          --lg-gold-light: #E8C97A;
          --lg-cream: #F7F5EF;
          --lg-border: #E5DFD0;
        }
        .swagger-wrapper {
          background: #F7F5EF;
          padding: 24px 0;
        }
        .swagger-wrapper .swagger-ui,
        .swagger-wrapper .swagger-ui .info .title,
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-operation-id,
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-path,
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-description,
        .swagger-wrapper .swagger-ui .opblock-tag,
        .swagger-wrapper .swagger-ui .btn,
        .swagger-wrapper .swagger-ui label,
        .swagger-wrapper .swagger-ui select,
        .swagger-wrapper .swagger-ui input,
        .swagger-wrapper .swagger-ui textarea,
        .swagger-wrapper .swagger-ui .parameter__name,
        .swagger-wrapper .swagger-ui .parameter__type,
        .swagger-wrapper .swagger-ui .response-col_status,
        .swagger-wrapper .swagger-ui .response-col_description,
        .swagger-wrapper .swagger-ui table thead tr th,
        .swagger-wrapper .swagger-ui table tbody tr td,
        .swagger-wrapper .swagger-ui .markdown p,
        .swagger-wrapper .swagger-ui .renderedMarkdown p {
          font-family: Inter, system-ui, sans-serif !important;
        }
        .swagger-wrapper .swagger-ui .topbar { display: none; }

        /* Info / header block */
        .swagger-wrapper .swagger-ui .info { margin: 30px 0; }
        .swagger-wrapper .swagger-ui .info .title,
        .swagger-wrapper .swagger-ui .info h1,
        .swagger-wrapper .swagger-ui .info h2 { color: #1E3A66; }
        .swagger-wrapper .swagger-ui .info a { color: #1E3A66; }
        .swagger-wrapper .swagger-ui .info a:hover { color: #D4A84B; }

        /* Scheme container (auth bar) */
        .swagger-wrapper .swagger-ui .scheme-container {
          background: #FFFFFF;
          border: 1px solid #E5DFD0;
          border-radius: 8px;
          box-shadow: 0 1px 2px rgba(30,58,102,0.05);
        }

        /* Tag headers (groups of endpoints) */
        .swagger-wrapper .swagger-ui .opblock-tag {
          color: #1E3A66;
          border-bottom: 1px solid #E5DFD0;
        }
        .swagger-wrapper .swagger-ui .opblock-tag:hover { background: rgba(212,168,75,0.06); }
        .swagger-wrapper .swagger-ui .opblock-tag small { color: #6B7280; }

        /* Operation blocks */
        .swagger-wrapper .swagger-ui .opblock {
          border-radius: 8px;
          border: 1px solid #E5DFD0;
          box-shadow: 0 1px 2px rgba(30,58,102,0.04);
          margin: 0 0 12px;
        }
        .swagger-wrapper .swagger-ui .opblock .opblock-summary { border-bottom: 1px solid transparent; }
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-path,
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-path__deprecated { color: #1E3A66; font-weight: 600; }
        .swagger-wrapper .swagger-ui .opblock .opblock-summary-description { color: #475569; }

        /* Method colors — navy/gold-tinted */
        .swagger-wrapper .swagger-ui .opblock.opblock-get { background: rgba(30,58,102,0.04); border-color: #1E3A66; }
        .swagger-wrapper .swagger-ui .opblock.opblock-get .opblock-summary-method { background: #1E3A66; }
        .swagger-wrapper .swagger-ui .opblock.opblock-post { background: rgba(212,168,75,0.06); border-color: #D4A84B; }
        .swagger-wrapper .swagger-ui .opblock.opblock-post .opblock-summary-method { background: #D4A84B; color: #1E3A66; }
        .swagger-wrapper .swagger-ui .opblock.opblock-patch { background: rgba(180,130,40,0.06); border-color: #B88B2E; }
        .swagger-wrapper .swagger-ui .opblock.opblock-patch .opblock-summary-method { background: #B88B2E; }
        .swagger-wrapper .swagger-ui .opblock.opblock-put { background: rgba(20,40,73,0.05); border-color: #142849; }
        .swagger-wrapper .swagger-ui .opblock.opblock-put .opblock-summary-method { background: #142849; }
        .swagger-wrapper .swagger-ui .opblock.opblock-delete { background: rgba(168,52,52,0.05); border-color: #A83434; }
        .swagger-wrapper .swagger-ui .opblock.opblock-delete .opblock-summary-method { background: #A83434; }

        /* Buttons */
        .swagger-wrapper .swagger-ui .btn {
          border-radius: 6px;
          font-weight: 600;
          box-shadow: none;
          transition: all 0.15s ease;
        }
        .swagger-wrapper .swagger-ui .btn.authorize {
          background: #1E3A66;
          color: #fff;
          border-color: #1E3A66;
        }
        .swagger-wrapper .swagger-ui .btn.authorize svg { fill: #fff; }
        .swagger-wrapper .swagger-ui .btn.authorize:hover { background: #142849; border-color: #142849; }
        .swagger-wrapper .swagger-ui .btn.execute {
          background: #D4A84B;
          color: #1E3A66;
          border-color: #D4A84B;
        }
        .swagger-wrapper .swagger-ui .btn.execute:hover { background: #B88B2E; border-color: #B88B2E; color: #fff; }
        .swagger-wrapper .swagger-ui .btn.try-out__btn {
          background: #fff;
          color: #1E3A66;
          border-color: #1E3A66;
        }
        .swagger-wrapper .swagger-ui .btn.try-out__btn:hover { background: #1E3A66; color: #fff; }
        .swagger-wrapper .swagger-ui .btn.cancel { color: #A83434; border-color: #A83434; }

        /* Inputs */
        .swagger-wrapper .swagger-ui input[type=text],
        .swagger-wrapper .swagger-ui input[type=password],
        .swagger-wrapper .swagger-ui input[type=email],
        .swagger-wrapper .swagger-ui input[type=number],
        .swagger-wrapper .swagger-ui textarea,
        .swagger-wrapper .swagger-ui select {
          border: 1px solid #E5DFD0;
          border-radius: 6px;
          background: #fff;
        }
        .swagger-wrapper .swagger-ui input[type=text]:focus,
        .swagger-wrapper .swagger-ui textarea:focus,
        .swagger-wrapper .swagger-ui select:focus {
          border-color: #D4A84B;
          outline: 2px solid rgba(212,168,75,0.25);
          outline-offset: 0;
        }

        /* Tables */
        .swagger-wrapper .swagger-ui table thead tr th {
          color: #1E3A66;
          border-bottom: 2px solid #E5DFD0;
        }
        .swagger-wrapper .swagger-ui .parameter__name { color: #1E3A66; font-weight: 600; }
        .swagger-wrapper .swagger-ui .parameter__name.required::after { color: #A83434; }
        .swagger-wrapper .swagger-ui .parameter__type { color: #B88B2E; }

        /* Response status colors */
        .swagger-wrapper .swagger-ui .response-col_status { color: #1E3A66; font-weight: 600; }
        .swagger-wrapper .swagger-ui .responses-inner h4,
        .swagger-wrapper .swagger-ui .responses-inner h5 { color: #1E3A66; }

        /* Code / highlighted JSON */
        .swagger-wrapper .swagger-ui .highlight-code,
        .swagger-wrapper .swagger-ui .microlight {
          background: #142849 !important;
          color: #F7F5EF !important;
          border-radius: 6px;
        }
        .swagger-wrapper .swagger-ui .opblock-body pre.microlight { background: #142849 !important; }

        /* Models section */
        .swagger-wrapper .swagger-ui section.models {
          border: 1px solid #E5DFD0;
          border-radius: 8px;
          background: #fff;
        }
        .swagger-wrapper .swagger-ui section.models h4,
        .swagger-wrapper .swagger-ui section.models .model-title { color: #1E3A66; }
        .swagger-wrapper .swagger-ui .model-toggle:after { background-color: #1E3A66; }

        /* Links */
        .swagger-wrapper .swagger-ui a { color: #1E3A66; }
        .swagger-wrapper .swagger-ui a:hover { color: #D4A84B; }

        /* Auth modal */
        .swagger-wrapper .swagger-ui .dialog-ux .modal-ux { border: 1px solid #E5DFD0; border-radius: 10px; }
        .swagger-wrapper .swagger-ui .dialog-ux .modal-ux-header h3 { color: #1E3A66; }
      `}</style>
    </div>
  );
}
